import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { PantallaCargando } from '@/components/Cargando';
import { Send, FileText, Globe, Zap, BarChart3, CheckCircle2, XCircle, Clock } from 'lucide-react';

/**
 * S89 — Inicio del módulo Email: acciones + métricas reales.
 */
export default function EmailHome() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/tenant/email/mass/stats').catch(() => ({ data: null })),
      api.get('/tenant/email/mass/sources-meta').catch(() => ({ data: null })),
    ]).then(([s, m]) => {
      setStats({ ...(s.data || {}), meta: m.data });
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <PantallaCargando />;

  const sent = stats?.enviados_30d ?? 0;
  const fail = stats?.fallidos_30d ?? 0;
  const total = sent + fail;
  const rate = total ? Math.round((sent / total) * 100) : null;
  const cupo = stats?.meta?.cupo || stats?.cupo || {};

  return (
    <div className="space-y-6" data-testid="email-home">
      <div>
        <h2 className="text-base font-semibold text-[#0F172A]">¿Qué quieres hacer?</h2>
        <p className="text-sm text-[#64748B] mt-1">Envíos masivos con tu marca. Simple y medible.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <ActionCard icon={Send} title="Nuevo envío" desc="Plantilla → a quién → cupo → enviar"
          onClick={() => navigate('/tenant/email/nuevo')} primary testId="email-home-nuevo" />
        <ActionCard icon={FileText} title="Plantillas" desc="Diseños y editor visual"
          onClick={() => navigate('/tenant/email/plantillas')} testId="email-home-plantillas" />
        <ActionCard icon={Zap} title="Automáticos" desc="Cumpleaños, citas, bienvenida"
          onClick={() => navigate('/tenant/email/automaticos')} testId="email-home-auto" />
        <ActionCard icon={Globe} title="Mi dominio" desc="CNAME y reputación SES"
          onClick={() => navigate('/tenant/email/dominio')} testId="email-home-dominio" />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-1.5">
            <BarChart3 className="w-4 h-4" /> Últimos 30 días
          </h3>
          <button type="button" className="text-[11px] text-[#F72585] underline"
            onClick={() => navigate('/tenant/email/informes')}>
            Ver informes →
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Metric label="Enviados" value={sent} color="text-emerald-600" icon={CheckCircle2} />
          <Metric label="Fallidos" value={fail} color="text-red-500" icon={XCircle} />
          <Metric label="Éxito" value={rate != null ? `${rate}%` : '—'} color="text-sky-600" icon={BarChart3} />
          <Metric label="Cupo restante" value={cupo.restantes ?? '—'} color="text-[#0F172A]" icon={Clock} />
        </div>
        {cupo.ok === false && cupo.motivo && (
          <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
            {cupo.motivo}
          </p>
        )}
      </div>

      {(stats?.ultimos || []).length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-[#0F172A] mb-2">Actividad reciente</h3>
          <div className="bg-white border border-[#E2E8F0] rounded-xl divide-y divide-[#F1F5F9]">
            {stats.ultimos.map((r, i) => (
              <div key={i} className="px-3 py-2.5 flex items-center justify-between gap-2 text-xs">
                <div className="min-w-0">
                  <p className="font-medium text-[#0F172A] truncate">{r.subject || r.template_key || 'Envío'}</p>
                  <p className="text-[#94A3B8]">{r.when}</p>
                </div>
                <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                  r.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'
                }`}>
                  {r.ok ? 'OK' : 'Falló'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ActionCard({ icon: Icon, title, desc, onClick, primary, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`text-left rounded-2xl border p-4 transition-colors ${
        primary
          ? 'border-[#F72585]/30 bg-[#FFF5F9] hover:border-[#F72585]'
          : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'
      }`}>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-2 ${
        primary ? 'bg-white border border-[#F72585]/20' : 'bg-[#F8FAFC]'
      }`}>
        <Icon className={`w-4 h-4 ${primary ? 'text-[#F72585]' : 'text-[#64748B]'}`} />
      </div>
      <p className="text-sm font-bold text-[#0F172A]">{title}</p>
      <p className="text-[11px] text-[#64748B] mt-0.5">{desc}</p>
    </button>
  );
}

function Metric({ label, value, color, icon: Icon }) {
  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-white p-3">
      <div className="flex items-center gap-1 text-[10px] text-[#94A3B8]">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
    </div>
  );
}
